import { SubmitClaim } from "commonAssets/components/Forms/SubmitClaim";
import { createElement } from "commonAssets/lib/createElement";
import { loadingGif } from "commonAssets/lib/loadingGif";
import { modal } from "commonAssets/lib/modal";
import { validateField } from "commonAssets/lib/validateField";
import { clearMessage } from "commonAssets/lib/clearMessage";
import { toggleFieldError } from "commonAssets/lib/toggleFieldError";
import { toggleFieldWarning } from "commonAssets/lib/toggleFieldWarning";
import { localDatetime } from "commonAssets/lib/localDatetime";
import { ConfirmEmailAddress } from "commonAssets/lib/ConfirmEmailAddress";
import {
    i18n,
    loading,
    outputDiv,
    reCAPTCHAv3,
    appPayMethod,
} from "commonAssets/js/constants";
import { dstProcess } from "commonAssets/components/FormBase/DigitalDisbursements";
import { ErrorHandler } from "commonAssets/utils/ErrorHandler";
import formDefault from "../../docs/formCreditBenefitRequest.json";

export class creditBenefitRequest extends SubmitClaim {
    async form() {
        const form = formDefault.claim_form[this.lang];
        this.init_claim(form);
    }

    /**
     * Render the general instructions always-visible (no collapse toggle).
     * This copy is required reading before the member signs, so the Credit
     * Benefit Request portal drops the parent's "Click for General
     * Instructions" accordion while keeping the #generalInstructionsWrapper id.
     */
    claim_instructions(params) {
        const instructions =
            params.form_options.claim_form.general_instructions;
        if (instructions.display === false) {
            return createElement("div", { class: "d-none" });
        }
        return createElement(
            "div",
            {
                class: "mb-3",
                id: "generalInstructionsWrapper",
            },
            [
                createElement(
                    "div",
                    {
                        class: "bg-light text-dark border rounded p-3 mb-3",
                    },
                    [],
                    instructions.body
                ),
            ]
        );
    }

    /**
     * Add the route-scoping form class so custom.css can target this form's
     * #certification etc. (mirrors updateEmail's "update-email" class).
     */
    claim_form(params) {
        const form = super.claim_form(params);
        form.classList.add("credit-benefit-request");
        return form;
    }

    /**
     * The Credit Benefit Request form drops the country/state/address fields.
     * The parent's toggle calls document.getElementById("country")
     * .addEventListener(...) with no null guard, so it would throw and abort
     * init_claim_fields before confirm-email setup runs. No-op when the
     * country select is absent.
     */
    setup_country_field_toggle() {
        if (!document.getElementById("country")) return;
        super.setup_country_field_toggle();
    }

    /**
     * Override the inherited populate_claim_form so it does NOT call the common
     * loadMember(), which hard-references #country (loadMember.js:105-106) and
     * would throw here — the Credit Benefit form removed the country/state/
     * address fields. Reproduces only what this portal needs: render the form,
     * prepopulate the member fields (load_member_fields), then init the fields.
     * The Digital Disbursements dstMember() step is intentionally omitted
     * (no payment widget; appPayMethod is "").
     */
    populate_claim_form(form, container, class_member, claim_deadline) {
        this.errorHandler.set_user({
            id: class_member.zero_zero_id,
            email: class_member.email_address,
        });

        document.getElementById("guard").remove();

        container.appendChild(this.claim_form(form));
        container.scrollIntoView({ behavior: "smooth" });
        outputDiv.appendChild(container);

        loadingGif(loading, "hide", 250);

        this.load_member_fields(class_member);

        if (this.customLoadMember != null) {
            this.customLoadMember(this.claim);
        }

        this.init_claim_fields(this.claim, form, claim_deadline);
    }

    /**
     * Prepopulate the trimmed demographic inputs from the class member record.
     * Mirrors common loadMember's member-key loop and the claim_id ->
     * confirm-email step, but WITHOUT its unguarded #country block. Then fills
     * and hides the address fields (see fill_hidden_admin_address).
     */
    load_member_fields(member) {
        for (const key in member) {
            if (!Object.prototype.hasOwnProperty.call(member, key)) continue;
            const field = document.getElementById(key);
            if (!field) continue;
            field.value = member[key];
        }

        const email = document.getElementById("email_address");
        const confirmEmail = document.getElementById("confirm_email_address");
        if (member.claim_id && email && confirmEmail) {
            confirmEmail.value = email.value;
        }

        this.fill_hidden_admin_address();
    }

    /**
     * The Credit Benefit form collects no address (counsel is not mailing these
     * members). But postClaimV4's Verifyd/Affirm fraud gate returns HTTP 400
     * when the payload has no mailing address, which surfaces as a generic
     * submit error. Fill the address fields with the Settlement Administrator's
     * own address (only when empty) and hide them, so the gate has a valid
     * address the member never sees or enters. For a known_class_member the
     * downstream address-validity reject is ignored, so only presence matters.
     */
    fill_hidden_admin_address() {
        const adminAddress = {
            street_address_1: "P.O. Box 58731",
            city: "Philadelphia",
            state: "PA",
            zip_code: "19102",
        };
        Object.entries(adminAddress).forEach(([id, value]) => {
            const field = document.getElementById(id);
            if (!field) return;
            if (!field.value) field.value = value;
            const wrapper = field.closest(".divContactField");
            if (wrapper) wrapper.classList.add("d-none");
        });
    }

    /**
     * Ops-specified confirmation ("copy of your request") page. Renders a
     * curated field list (Access ID + name + email + signature + date) instead
     * of the common full contact/claim-field dump, and omits the Correlation ID
     * row. Copy comes from the i18n overrides in languages.js.
     */
    claim_confirmation(params) {
        const cp = i18n.getTranslation("confirmation_page");
        return createElement(
            "div",
            {
                class: "bg-light text-dark border rounded p-3 mb-3",
                id: "confirmation",
            },
            [
                createElement("h3", { class: "text-center mb-3" }, [], cp.title),
                createElement(
                    "h4",
                    { class: "alert alert-success text-center mb-3" },
                    [],
                    i18n.getTranslation("confirmation_this_is_a_copy")
                ),
                createElement(
                    "p",
                    { class: "text-center fw-bold mb-3" },
                    [],
                    cp.print_note
                ),
                createElement(
                    "h4",
                    { class: "text-center fw-bold mb-3" },
                    [],
                    cp.claim_details.title
                ),
                createElement(
                    "p",
                    { class: "text-center fw-bold mb-3" },
                    [],
                    cp.claim_details.body
                ),
                this.confirmation_row("Access ID", params.zero_zero_id),
                this.confirmation_row("First Name", params.contact.first_name),
                this.confirmation_row("Last Name", params.contact.last_name),
                this.confirmation_row(
                    "Email Address",
                    params.contact.email_address
                ),
                this.confirmation_row("Signature", params.contact.signature),
                this.confirmation_row("Date", params.contact.date),
                createElement(
                    "p",
                    { class: "text-center mb-3" },
                    [],
                    cp.claim_details.footer
                ),
            ]
        );
    }

    confirmation_row(label, value) {
        return createElement("div", { class: "row mb-3 pb-3 border-bottom" }, [
            createElement("div", { class: "col fw-bold text-end" }, [], label),
            createElement("div", { class: "col" }, [], value || ""),
        ]);
    }

    init_claim(form) {
        this.claim.api
            .claimsDeadlineGet({ domain: this.claim.data.domain })
            .then((result) => {
                // Claim Deadline - Default Pacific DateTime
                let datetime;
                if (Boolean(result.data.pacific_datetime)) {
                    datetime = result.data.pacific_datetime;
                } else {
                    datetime = `<pre>Error: datetime</pre>`;
                }

                let claim_deadline;
                if (Boolean(result.data.claim_deadline)) {
                    claim_deadline = result.data.claim_deadline;
                }

                // Error Handling
                if (result.data.error) {
                    this.setup_result_data_error(result);
                    return;
                }

                const container = createElement("div", {
                    class: "display-form",
                });

                /**
                 * Claim Guard
                 */
                if (form.form_options.notice_confirmation.display === true) {
                    // Guard
                    container.appendChild(this.guard(form, datetime));
                    outputDiv.appendChild(container);

                    // Deficiency portal: drop the claim-deadline banner
                    // (#claim_form_alert) that guard() renders — it's noise for
                    // the Credit Benefit Request flow.
                    document.getElementById("claim_form_alert")?.remove();

                    const zero_zero_id =
                        document.getElementById("zero_zero_id");
                    const confirmation_code =
                        document.getElementById("confirmation_code");
                    const skip_guard = document.getElementById("skip-guard");

                    // Notice Id and Confirmation Code
                    [zero_zero_id, confirmation_code].forEach(function (input) {
                        input.addEventListener("change", validateField);
                    });

                    // Skip Guard
                    if (skip_guard) {
                        skip_guard.addEventListener("click", () => {
                            // Hide guard
                            // document.getElementById('guard').classList.add("d-none");
                            document.getElementById("guard").remove();
                            // New Claim Form
                            container.appendChild(
                                this.claim_form(form, datetime)
                            );
                            container.scrollIntoView({ behavior: "smooth" });
                            outputDiv.appendChild(container);

                            // Init claim fields
                            this.init_claim_fields(
                                this.claim,
                                form,
                                claim_deadline
                            );
                        });
                    }

                    // ada compliance updates - added
                    // if input#submit-guard was clicked add .was-verified to form#guard
                    document
                        .getElementById("submit-guard")
                        .addEventListener("click", () => {
                            document
                                .getElementById("guard")
                                .classList.add("was-validated");
                            // TODO: add required text to labels
                        });

                    // Validate Guard Fields
                    document
                        .getElementById("guard")
                        .addEventListener("submit", (e) => {
                            e.preventDefault();

                            let valid = true;
                            const member = {
                                domain: this.claim.data.domain,
                                zero_zero_id: zero_zero_id.value.toUpperCase(),
                                confirmation_code: confirmation_code.value,
                            };

                            clearMessage();

                            // Validate guard fields
                            [zero_zero_id, confirmation_code].forEach(
                                function (input) {
                                    valid = validateField(input) && valid;
                                }
                            );
                            if (!valid) return;

                            loadingGif(loading, "show");

                            // Retrieve class member data (use POST to pass notice id and confirmation code privately)
                            this.claim.api
                                .classMemberPost({}, member)
                                .then((result) => {
                                    // Check for error message
                                    if (result.data.error) {
                                        toggleFieldError(
                                            zero_zero_id,
                                            true,
                                            ""
                                        );
                                        toggleFieldError(
                                            confirmation_code,
                                            true,
                                            ""
                                        );
                                        this.errorHandler.log_exception(
                                            new Error(
                                                "Error loading class member",
                                                { cause: result }
                                            )
                                        );
                                        this.errorHandler.display_modal(result);
                                        return;
                                    }

                                    // Add notice id and confirmation code to claim data
                                    this.claim.data.zero_zero_id =
                                        member.zero_zero_id;
                                    this.claim.data.confirmation_code =
                                        member.confirmation_code;

                                    // Define the environment for the OTP
                                    const environment =
                                        this.claim.data.domain.split(".")[0]; // ada compliance - added

                                    // Deficiency gate: allow only an active
                                    // Deficient status flagged for the Credit
                                    // Benefit Request. cm_data is nested under
                                    // class_member on the claim_id response,
                                    // top-level otherwise.
                                    let cmData =
                                        result.data?.class_member?.cm_data ??
                                        result.data?.cm_data ??
                                        {};
                                    // cm_data may arrive as a JSON string;
                                    // parse before property access so a string
                                    // payload does not silently block eligible
                                    // users.
                                    if (typeof cmData === "string") {
                                        try {
                                            cmData = JSON.parse(cmData);
                                        } catch {
                                            cmData = {};
                                        }
                                    }
                                    // ClassMemberStatus may be a single object
                                    // rather than an array; normalize so .some
                                    // never throws.
                                    const statuses = Array.isArray(
                                        cmData?.ClassMemberStatus
                                    )
                                        ? cmData.ClassMemberStatus
                                        : cmData?.ClassMemberStatus
                                          ? [cmData.ClassMemberStatus]
                                          : [];
                                    const hasAccess = statuses.some(
                                        (status) =>
                                            status &&
                                            status.Active === true &&
                                            status.Label === "Deficient" &&
                                            status.CanEdit === true &&
                                            status.Reason === "BenefitRequest"
                                    );
                                    if (!hasAccess) {
                                        loadingGif(loading, "hide", 250);
                                        modal(
                                            i18n.getTranslation("error"),
                                            `<p>This Notice ID is not eligible to access a Credit Benefit.</p><p>If you believe this is an error, contact the Settlement Administrator at <a href="mailto:Info@2025PriceSettlement.com">Info@2025PriceSettlement.com</a>.</p>`
                                        );
                                        return;
                                    }

                                    if (result.data.claim_id) {
                                        this.claim.data.claim_id =
                                            result.data.claim_id;
                                        this.claim.class_member = result.data;
                                        if (environment !== "local") {
                                            this.send_otp(
                                                form,
                                                container,
                                                result.data,
                                                claim_deadline
                                            );
                                            return;
                                        }
                                    }

                                    // Populate claim form
                                    this.populate_claim_form(
                                        form,
                                        container,
                                        result.data,
                                        claim_deadline
                                    );
                                })
                                .catch((error) => {
                                    this.errorHandler.log_exception(
                                        new Error(
                                            "Error loading class member",
                                            { cause: error }
                                        )
                                    );
                                    this.errorHandler.display_modal(error);
                                });
                        });
                } else {
                    /**
                     * No Claim Guard
                     */
                    // Edit claim link
                    this.edit_claim(form);

                    // New Claim Form
                    container.appendChild(this.claim_form(form));
                    outputDiv.appendChild(container);

                    // Init claim fields
                    this.init_claim_fields(this.claim, form, claim_deadline);
                }

                loadingGif(loading, "hide", 250);
            })
            .catch((error) => {
                this.errorHandler.log_exception(
                    new Error("Error fetching claim deadline", { cause: error })
                );
                this.errorHandler.display(error);
            });
    }

    async process_claim(e, claim, deadline) {
        console.log("[process_claim]");

        e.preventDefault();

        document.getElementById("submit-claim").disabled = true;

        if (claim.flags.sent) {
            return;
        }

        clearMessage();

        claim.flags.valid = true;
        claim.data.fields = {};

        this.init_claim_inputs();

        this.format_claim_data(claim);

        // Stamp one timestamp and reuse it so the contact date and the hidden
        // request-date field cannot straddle a second boundary.
        const submittedAt = localDatetime();
        claim.data.contact.date = submittedAt;

        // Hidden claim field: stamp the request date into the claim data (the
        // form collects no claim fields, so inject it directly rather than
        // render a #claim-fields panel).
        claim.data.fields["Credit Benefit Request Date"] = submittedAt;

        if (document.getElementById("documentation")) {
            this.process_claim_files(this.claim);
        }

        if (
            document.getElementById("email_address") &&
            document.getElementById("confirm_email_address")
        ) {
            ConfirmEmailAddress("process", this.claim);
        }

        if (
            appPayMethod &&
            appPayMethod !== "" &&
            this.customDstProcess === null
        ) {
            claim = dstProcess(this.claim);
        } else if (
            appPayMethod &&
            appPayMethod !== "" &&
            this.customDstProcess !== null
        ) {
            claim = this.customDstProcess(this.claim);
        }

        const s3env = this.config.url;

        // Custom process claim with async support
        if (this.customProcessClaim != null) {
            try {
                const maybeClaim = this.customProcessClaim(this.claim);
                claim =
                    maybeClaim && typeof maybeClaim.then === "function"
                        ? await maybeClaim
                        : maybeClaim;
            } catch (error) {
                claim.flags.valid = false;
                this.errorHandler.log_exception(
                    new Error("Error in custom process claim", { cause: error })
                );
                this.errorHandler.display_modal(error);
            }
        }

        if (claim.flags.valid) {
            try {
                const token = await grecaptcha.enterprise.execute(reCAPTCHAv3, {
                    action: "submit",
                });
                claim.data.token = token;
                this.post_claim(claim, s3env);
            } catch (error) {
                document.getElementById("submit-claim").disabled = false;
                this.errorHandler.log_exception(
                    new Error("Error retrieving reCAPTCHA token", {
                        cause: error,
                    })
                );
                this.errorHandler.display_modal(error);
            }
        } else {
            document.getElementById("submit-claim").disabled = false;
            if (!claim.flags.suppress_error_modal) {
                modal(
                    i18n.getTranslation("error"),
                    i18n.getTranslation("claim_form_errors")
                );
            }
        }
    }

    static createInstance({
        appClaimForm,
        userLanguage,
        customInitClaim = null,
        customProcessClaim = null,
        customLoadMember = null,
        customDstProcess = null,
        errorHandler = new ErrorHandler(),
    }) {
        return new creditBenefitRequest(
            appClaimForm,
            userLanguage,
            customInitClaim,
            customProcessClaim,
            customLoadMember,
            customDstProcess,
            errorHandler
        );
    }
}
